import React, { useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import i18n from 'i18next';
import './charts.css';

const ModernBarChart = ({ data, title, namePrefix = '' }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    // Transform data for Recharts
    const chartData = Object.keys(data).map((key, index) => ({
        name: i18n.t(`${namePrefix}${key}`),
        value: data[key],
        originalKey: key
    }));

    // Gradient colors for bars
    const COLORS = [
        '#00897B', '#26C6DA', '#7DD3C0', '#00BFA5', '#80CBC4',
        '#4DB6AC', '#64B5F6', '#81C784', '#FFB74D', '#A1887F'
    ];

    // Recharts injects these as SVG attributes — CSS variables don't resolve
    // in SVG style attrs, so the chart-internal palette has to live as JS
    // literals. Hoist into one place so a future palette pass has a single
    // touchpoint instead of seven.
    const CHART_AXIS_FILL    = '#546e7a';   // tick label colour (= --text-secondary)
    const CHART_AXIS_STROKE  = '#b2dfdb';   // axis line       (= --border-color)
    const CHART_GRID_STROKE  = '#e0f2f1';   // background grid (= soft teal tint)
    const CHART_HOVER_CURSOR = 'rgba(0, 137, 123, 0.15)';

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="chart-tooltip">
                    <p className="chart-tooltip__title">
                        {payload[0].payload.name}
                    </p>
                    <p className="chart-tooltip__accent">
                        {i18n.t('n')}: {payload[0].value}
                    </p>
                </div>
            );
        }
        return null;
    };

    // Custom label on top of bars
    const renderCustomLabel = (props) => {
        const { x, y, width, value } = props;
        return (
            <text
                x={x + width / 2}
                y={y - 5}
                fill={CHART_AXIS_FILL}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: '12px', fontWeight: '600' }}
            >
                {value}
            </text>
        );
    };

    // Chart sits inside Accordion.Item which already provides the glass surface,
    // padding, and the title. A second glass plate + duplicate title here was
    // the source of the "patches of blur" + duplicated headings on /patients & /assays.
    return (
        <div style={{ width: '100%' }}>
            <ResponsiveContainer width="100%" height={400}>
                <BarChart
                    data={chartData}
                    margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
                    <XAxis
                        dataKey="name"
                        tick={{ fill: CHART_AXIS_FILL, fontSize: 12 }}
                        axisLine={{ stroke: CHART_AXIS_STROKE }}
                    />
                    <YAxis
                        tick={{ fill: CHART_AXIS_FILL, fontSize: 12 }}
                        axisLine={{ stroke: CHART_AXIS_STROKE }}
                        label={{
                            value: i18n.t('n'),
                            angle: -90,
                            position: 'insideLeft',
                            style: { fill: CHART_AXIS_FILL, fontSize: 14, fontWeight: '600' }
                        }}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: CHART_HOVER_CURSOR }} />
                    <Bar
                        dataKey="value"
                        label={renderCustomLabel}
                        radius={[8, 8, 0, 0]}
                        animationBegin={0}
                        animationDuration={800}
                        onMouseEnter={(data, index) => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(null)}
                    >
                        {chartData.map((entry, index) => {
                            const isActive = activeIndex === index;
                            const className = `chart-bar-cell${isActive ? ' chart-bar-cell--active' : ''}${activeIndex !== null && !isActive ? ' chart-bar-cell--dim' : ''}`;
                            return (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                    className={className}
                                />
                            );
                        })}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ModernBarChart;
